import { ModelFieldInputType, ModelFieldsProjection, } from '@shared/modelField/modelField';
import { optionalTranslatableEnumTransform } from '@shared/utils/transform';
import { VerandaPanelGlazingType, VerandaPanelMaterial, } from '@shared/types/api/externalDamage';
import { ApiEntityType } from '@shared/types/api/apiEntityType';
import { SWIMPOOL_COVER_GLASS_PANEL_GLAZING_CHOICES, SWIMPOOL_COVER_GLASS_PANEL_GLAZING_TRAD_KEYS_AND_IMAGES, SWIMPOOL_COVER_PANELS_MATERIAL_CHOICES, SWIMPOOL_COVER_PANELS_MATERIAL_TRAD_KEYS_AND_IMAGES, } from '@shared/constants/externalDamages/SwimpoolCoverConstants';
import { translateChoices } from '@shared/choice';
import { formatOptionalLenght } from '@shared/utils/formatLength';
import { isApiResourceOfType } from '@shared/utils/isApiResource';
/**
 * [CLAIMS_CONTEXT]
 * purpose: Expose editable damaged-panel data and panel summaries for external swimming pool cover damages.
 * touches: declaration, backoffice edition
 * invariants: swimming pool cover fields follow the veranda panel pattern without structure or gutter fields
 * inputs: SwimpoolCoverDamage model
 * outputs: model fields used by declaration and backoffice forms
 * see: frontend/src/shared/externalDamage/verandaFields.ts, frontend/src/shared/types/api/claim.ts
 */
const panelsFromModel = (model) => {
    var _a, _b;
    return isApiResourceOfType(model, ApiEntityType.SwimpoolCoverDamage)
        ? (_b = (_a = model.panels) === null || _a === void 0 ? void 0 : _a.slice()) !== null && _b !== void 0 ? _b : []
        : [];
};
const swimpoolCoverPanelFields = (model, index, panels, { translate }) => {
    const panel = model;
    const fields = [
        {
            label: 'Matériau',
            value: panel.material,
            displayedValue: optionalTranslatableEnumTransform(panel.material, SWIMPOOL_COVER_PANELS_MATERIAL_TRAD_KEYS_AND_IMAGES, translate, '-'),
            editable: {
                input: {
                    type: ModelFieldInputType.Radio,
                    vertical: true,
                    choices: translateChoices(SWIMPOOL_COVER_PANELS_MATERIAL_CHOICES, translate),
                },
                modelKey: `panels.${index}.material`,
                toPayload: (material) => {
                    var _a, _b;
                    const payload = { material };
                    if (material === VerandaPanelMaterial.Glass) {
                        const glazingType = (_b = (_a = panels.find((currentPanel) => currentPanel.material === VerandaPanelMaterial.Glass && currentPanel.glazingType)) === null || _a === void 0 ? void 0 : _a.glazingType) !== null && _b !== void 0 ? _b : null;
                        payload.glazingType = glazingType;
                    }
                    else {
                        payload.glazingType = null;
                    }
                    if (material === VerandaPanelMaterial.Other) {
                        payload.width = null;
                        payload.height = null;
                    }
                    return payload;
                },
            },
        },
    ];
    if ([
        VerandaPanelMaterial.Glass,
        VerandaPanelMaterial.Polycarbonate,
        VerandaPanelMaterial.SandwichPanel,
    ].includes(panel.material)) {
        fields.push({
            label: 'Hauteur',
            value: panel.height ? panel.height / 10 : null,
            displayedValue: formatOptionalLenght(panel.height ? panel.height / 10 : null, 'cm', '-'),
            editable: {
                unit: 'cm',
                input: { type: ModelFieldInputType.Number },
                modelKey: `panels.${index}.height`,
                toPayload: (height) => ({ height: height * 10 }),
            },
        }, {
            label: 'Largeur',
            value: panel.width ? panel.width / 10 : null,
            displayedValue: formatOptionalLenght(panel.width ? panel.width / 10 : null, 'cm', '-'),
            editable: {
                unit: 'cm',
                input: { type: ModelFieldInputType.Number },
                modelKey: `panels.${index}.width`,
                toPayload: (width) => ({ width: width * 10 }),
            },
        });
    }
    return {
        title: `Panneau n°${index + 1}`,
        model,
        fields,
        editable: {
            toPayload: (value, model) => {
                const currentPanels = panelsFromModel(model);
                const current = currentPanels[index];
                currentPanels.splice(index, 1, { ...current, ...value });
                return { panels: currentPanels };
            },
        },
    };
};
export const swimpoolCoverFields = (model, context) => {
    var _a, _b, _c;
    const { translate, projection } = context;
    const fields = [];
    if (projection !== ModelFieldsProjection.Declaration) {
        fields.push({
            label: translate('model.external_damage.type'),
            key: 'type',
            value: model.slug,
            displayedValue: translate('claim.declaration.external_damages.form.slug.swimpool_cover'),
        });
    }
    const modelPanels = panelsFromModel(model);
    if (projection === ModelFieldsProjection.Backoffice) {
        const modelFields = modelPanels.map((panel, index, panels) => swimpoolCoverPanelFields(panel, index, panels, context));
        fields.push({
            label: 'Panneaux endommagés',
            emptyString: 'Aucun panneau endommagé',
            addLabel: 'Ajouter',
            modelFields,
            editable: {
                add: (model) => {
                    const panels = panelsFromModel(model);
                    panels.push({
                        '@type': ApiEntityType.SwimpoolCoverDamagePanel,
                        material: null,
                        width: null,
                        height: null,
                        glazingType: null,
                    });
                    return { panels };
                },
                delete: (model, index) => {
                    const panels = panelsFromModel(model);
                    panels.splice(index, 1);
                    return { panels };
                },
                toPayload: (value, model, index) => {
                    const panels = panelsFromModel(model);
                    const current = panels[index];
                    panels.splice(index, 1, { ...current, ...value });
                    return { panels };
                },
                modelKey: 'panels',
            },
        });
        const glassPanels = (_a = modelPanels === null || modelPanels === void 0 ? void 0 : modelPanels.filter((panel) => (panel === null || panel === void 0 ? void 0 : panel.material) === VerandaPanelMaterial.Glass)) !== null && _a !== void 0 ? _a : [];
        if (glassPanels.length) {
            const glassPanel = glassPanels[0];
            const index = modelPanels.indexOf(glassPanel);
            if (index > -1) {
                const glazingType = (_b = glassPanel === null || glassPanel === void 0 ? void 0 : glassPanel.glazingType) !== null && _b !== void 0 ? _b : null;
                fields.push({
                    label: 'Vitrage des panneaux en verre',
                    value: glazingType,
                    displayedValue: optionalTranslatableEnumTransform(glazingType, SWIMPOOL_COVER_GLASS_PANEL_GLAZING_TRAD_KEYS_AND_IMAGES, translate),
                    editable: {
                        input: {
                            type: ModelFieldInputType.Radio,
                            vertical: true,
                            choices: translateChoices(SWIMPOOL_COVER_GLASS_PANEL_GLAZING_CHOICES, translate),
                        },
                        modelKey: `panels.${index}.glazingType`,
                        toPayload: (nextGlazingType) => {
                            return {
                                panels: modelPanels.map((panel) => {
                                    if (panel.material === VerandaPanelMaterial.Glass)
                                        return { ...panel, glazingType: nextGlazingType };
                                    return panel;
                                }),
                            };
                        },
                    },
                });
            }
        }
        return fields;
    }
    const glassPanels = modelPanels.filter((panel) => (panel === null || panel === void 0 ? void 0 : panel.material) === VerandaPanelMaterial.Glass);
    const polycarbonatePanels = modelPanels.filter((panel) => (panel === null || panel === void 0 ? void 0 : panel.material) === VerandaPanelMaterial.Polycarbonate);
    const sandwichPanels = modelPanels.filter((panel) => (panel === null || panel === void 0 ? void 0 : panel.material) === VerandaPanelMaterial.SandwichPanel);
    const otherPanels = modelPanels.filter((panel) => (panel === null || panel === void 0 ? void 0 : panel.material) === VerandaPanelMaterial.Other);
    const panelLabels = [];
    if (glassPanels.length) {
        let label = translate(SWIMPOOL_COVER_PANELS_MATERIAL_TRAD_KEYS_AND_IMAGES.glass.tradKey);
        const glazingType = (_c = glassPanels.find((panel) => panel === null || panel === void 0 ? void 0 : panel.glazingType)) === null || _c === void 0 ? void 0 : _c.glazingType;
        if (glazingType && glazingType !== VerandaPanelGlazingType.Unknown) {
            label += ` ${translate(`glazing.${glazingType}`).toLocaleLowerCase()}`;
        }
        panelLabels.push(`${label} ×${glassPanels.length}`);
    }
    if (polycarbonatePanels.length) {
        panelLabels.push(`${translate(SWIMPOOL_COVER_PANELS_MATERIAL_TRAD_KEYS_AND_IMAGES.polycarbonate.tradKey)} ×${polycarbonatePanels.length}`);
    }
    if (sandwichPanels.length) {
        panelLabels.push(`${translate(SWIMPOOL_COVER_PANELS_MATERIAL_TRAD_KEYS_AND_IMAGES['sandwich-panel'].tradKey)} ×${sandwichPanels.length}`);
    }
    if (otherPanels.length) {
        panelLabels.push(`${translate(SWIMPOOL_COVER_PANELS_MATERIAL_TRAD_KEYS_AND_IMAGES.other.tradKey)} ×${otherPanels.length}`);
    }
    fields.push({
        label: 'Panneaux endommagés',
        displayedValue: panelLabels.length ? panelLabels.join(', ') : translate('no'),
        key: 'panels',
    });
    return fields;
};
